import { BaseService } from './BaseService';
import type { ApiEnvelope } from '@/api/errors';
import type { NotificationItem, NotificationPayload } from '@/types/notifications';

// Уведомления (notification-service). История + отметки прочтения. SSE-поток — отдельно
// (realtime/sseClient), т.к. это стрим, а не запрос. Эндпоинты сверены с openapi notification-service.

interface NotificationDto {
  readonly id?: string;
  readonly type?: string;
  readonly createdDate?: string;
  readonly isRead?: boolean;
  readonly displayable?: boolean;
  readonly payload?: NotificationPayload;
}

export interface NotificationListParams {
  /** По умолчанию бэк отдаёт только видимые (displayable=true). */
  readonly displayable?: boolean;
  readonly type?: string;
  readonly isRead?: boolean;
}

function toItem(dto: NotificationDto): NotificationItem {
  return {
    id: dto.id ?? '',
    type: (dto.type ?? '').toLowerCase(),
    createdDate: dto.createdDate,
    isRead: dto.isRead ?? false,
    displayable: dto.displayable,
    payload: dto.payload,
  };
}

class NotificationService extends BaseService {
  /** История видимых уведомлений (бэк ограничивает последними 7 днями). GET — идемпотентно. */
  async list(
    params: NotificationListParams = { displayable: true },
    signal?: AbortSignal,
  ): Promise<NotificationItem[]> {
    const res = await this.request<ApiEnvelope<NotificationDto[]>>(
      { method: 'get', url: '/notification/list', params, signal },
      { idempotent: true },
    );
    // Пустой список — норма (не unwrap: тот бросил бы на пустых data). Логическую ошибку всё же ловим.
    this.ensureSuccess(res.data, res.status);
    return (res.data?.data ?? []).filter((dto) => dto.id).map(toItem);
  }

  /** Пометить одно уведомление прочитанным (бэк также ставит delivered=true). */
  async read(notificationId: string): Promise<void> {
    const res = await this.request<ApiEnvelope>(
      { method: 'post', url: '/notification/read', data: { notificationId } },
      { retry: false },
    );
    this.ensureSuccess(res.data, res.status);
  }

  /** Пометить все видимые уведомления прочитанными. */
  async readAll(): Promise<void> {
    const res = await this.request<ApiEnvelope>(
      { method: 'post', url: '/notification/readAll', data: {} },
      { retry: false },
    );
    this.ensureSuccess(res.data, res.status);
  }

  /**
   * Подтвердить доставку одного уведомления (ставит delivered=true, is_read не трогает).
   * Гасит SSE-догон: notification-service пере-присылает при реконнекте только недоставленные,
   * поэтому без подтверждения системные/скрытые события копились бы и слались снова и снова.
   */
  async confirm(notificationId: string): Promise<void> {
    const res = await this.request<ApiEnvelope>(
      { method: 'post', url: '/notification/confirm', data: { notificationId } },
      { retry: false },
    );
    this.ensureSuccess(res.data, res.status);
  }
}

export const notificationService = new NotificationService();
